import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormFieldComponent } from '../form-field/form-field.component';
import { FormFieldCurrencyComponent } from '../form-field-currency/form-field-currency.component';
import { environment } from '../../../../environments/environment';

interface EnquiryForm {
  fullName: FormControl<string | null>;
  email: FormControl<string | null>;
  mobile: FormControl<string | null>;
  amountToBorrow: FormControl<number | null>;
  loanDescription: FormControl<string | null>;
  publicId: FormControl<string | null>;
  dealershipCode: FormControl<string | null>;
}

@Component({
  selector: 'app-enquiry-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    FormFieldComponent,
    FormFieldCurrencyComponent
  ],
  templateUrl: './enquiry-dialog.component.html',
  styleUrl: './enquiry-dialog.component.scss'
})
export class EnquiryDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('turnstileWidget') turnstileEl!: ElementRef;

  form: FormGroup<EnquiryForm>;
  turnstileToken: string | null = null;
  turnstileError = false;
  submitState: 'idle' | 'success' | 'error' = 'idle';
  errorMessage: string | null = null;
  private widgetId: string | null = null;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EnquiryDialogComponent>
  ) {
    const params = new URLSearchParams(window.location.search);
    this.form = this.fb.group<EnquiryForm>({
      fullName: this.fb.control<string | null>(null, Validators.required),
      email: this.fb.control<string | null>(null, [Validators.required, Validators.email]),
      mobile: this.fb.control<string | null>(null, Validators.required),
      amountToBorrow: this.fb.control<number | null>(null),
      loanDescription: this.fb.control<string | null>(null),
      publicId: this.fb.control<string | null>(params.get('publicId')),
      dealershipCode: this.fb.control<string | null>(params.get('dealershipCode'))
    });
  }

  ngAfterViewInit(): void {
    this.renderWidget();
  }

  ngOnDestroy(): void {
    const turnstile = (window as any).turnstile;
    if (turnstile && this.widgetId) {
      turnstile.remove(this.widgetId);
    }
  }

  private renderWidget(): void {
    const turnstile = (window as any).turnstile;
    if (turnstile) {
      this.widgetId = turnstile.render(this.turnstileEl.nativeElement, {
        sitekey: environment.turnstileSiteKey,
        callback: (token: string) => {
          this.turnstileToken = token;
          this.turnstileError = false;
        },
        'expired-callback': () => {
          this.turnstileToken = null;
        },
        'error-callback': () => {
          this.turnstileToken = null;
        }
      });
    } else {
      // Script still loading — retry shortly
      setTimeout(() => this.renderWidget(), 100);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.turnstileToken) {
      this.turnstileError = true;
      return;
    }

    // TODO: replace with actual API call, e.g.:
    // this.http.post('/api/enquiry', { ...this.form.value, turnstileToken: this.turnstileToken })
    //   .subscribe({
    //     next: () => this.submitState = 'success',
    //     error: (err) => {
    //       this.errorMessage = err?.error?.message ?? null;
    //       this.submitState = 'error';
    //     }
    //   });

    this.submitState = 'success';
  }
}
